import {
  MOCK_UNIVERSITIES,
  MOCK_CAMPUSES,
  MOCK_FACULTIES,
  MOCK_DEPARTMENTS,
  MOCK_CLUBS,
  MOCK_CLUB_DETAILS,
  MOCK_EVENTS,
  MOCK_TRANSFERS,
  MOCK_MONTHLY_ANALYTICS,
  MOCK_USERS_UM,
  MOCK_CLUB_MEMBERSHIPS,
  MOCK_ACTIVITY_LOGS,
} from "./mockData";
import { MOCK_TRANSACTIONS } from "./mockTransactions";

let events = MOCK_EVENTS.map((item) => ({ ...item }));
let universities = MOCK_UNIVERSITIES.map((item) => ({ ...item }));
let campuses = MOCK_CAMPUSES.map((item) => ({ ...item }));
let faculties = MOCK_FACULTIES.map((item) => ({ ...item }));
let departments = MOCK_DEPARTMENTS.map((item) => ({ ...item }));
let clubs = MOCK_CLUBS.map((item) => ({ ...item, ...(MOCK_CLUB_DETAILS[item.id] || {}) }));
let transfers = MOCK_TRANSFERS.map((item) => ({ ...item }));
let users = MOCK_USERS_UM.map((item) => ({ ...item }));
let clubMemberships = MOCK_CLUB_MEMBERSHIPS.map((item) => ({ ...item }));
let activityLogs = MOCK_ACTIVITY_LOGS.map((item) => ({ ...item, metadata: item.metadata ? { ...item.metadata } : undefined }));
let transactions = MOCK_TRANSACTIONS.map((item) => ({ ...item }));

// Build lookup maps for local name enrichment
const _uniLocalMap = Object.fromEntries(universities.map((u) => [u.id, u.nameLocal]));
const _campusLocalMap = Object.fromEntries(campuses.map((c) => [c.id, c.nameLocal]));
const _facultyLocalMap = Object.fromEntries(faculties.map((f) => [f.id, f.nameLocal]));
const _clubLocalMap = Object.fromEntries(clubs.map((c) => [c.id, c.nameLocal]));

// Enrich all entity arrays with local reference names
campuses = campuses.map((c) => ({ ...c, universityNameLocal: _uniLocalMap[c.universityId] }));
faculties = faculties.map((f) => ({ ...f, campusNameLocal: _campusLocalMap[f.campusId], universityNameLocal: _uniLocalMap[f.universityId] }));
departments = departments.map((d) => ({ ...d, campusNameLocal: _campusLocalMap[d.campusId], facultyNameLocal: _facultyLocalMap[d.facultyId], universityNameLocal: _uniLocalMap[d.universityId] }));
clubs = clubs.map((c) => ({ ...c, campusNameLocal: c.campusNameLocal || _campusLocalMap[c.campusId], universityNameLocal: c.universityNameLocal || _uniLocalMap[c.universityId] }));
events = events.map((e) => ({ ...e, campusNameLocal: _campusLocalMap[e.campusId], universityNameLocal: _uniLocalMap[e.universityId], clubNameLocal: _clubLocalMap[e.clubId] }));

const delay = (ms = 200) => new Promise((resolve) => setTimeout(resolve, ms));

const paginate = (arr, page = 0, size = 10) => {
  const start = page * size;
  const content = arr.slice(start, start + size);
  return {
    success: true,
    data: {
      content,
      totalPages: Math.max(1, Math.ceil(arr.length / size)),
      totalElements: arr.length,
    },
  };
};

const filterByTenant = (arr, { userRole, currentTenantId } = {}) => {
  if (userRole === "UNIVERSITY_MANAGER" && currentTenantId != null) {
    return arr.filter((item) => item.universityId === Number(currentTenantId));
  }
  return arr;
};

const getCampusById = (id) => campuses.find((item) => item.id === Number(id));
const getFacultyById = (id) => faculties.find((item) => item.id === Number(id));
const getDepartmentById = (id) => departments.find((item) => item.id === Number(id));
const getClubById = (id) => clubs.find((item) => item.id === Number(id));
const getUserById = (id) => users.find((item) => item.id === Number(id));

const toDateStart = (value) => (value ? new Date(`${value}T00:00:00`) : null);
const toDateEnd = (value) => (value ? new Date(`${value}T23:59:59.999`) : null);

const getDateKey = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const buildMonthlyTrend = (items, dateField, valueFn = () => 1) => {
  const map = new Map();
  items.forEach((item) => {
    const raw = item?.[dateField];
    if (!raw) return;
    const month = getDateKey(raw).slice(0, 7);
    if (!month) return;
    const previous = map.get(month) || 0;
    map.set(month, previous + Number(valueFn(item) || 0));
  });

  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, value]) => ({ month, value }));
};

const sanitizeText = (value) => (value == null ? "" : String(value).trim());

const appendActivityLog = (entry) => {
  const nextId = activityLogs.reduce((max, item) => Math.max(max, item.id), 0) + 1;
  const log = {
    id: nextId,
    timestamp: new Date().toISOString(),
    actorRole: "UNIVERSITY_MANAGER",
    actorName: "University Manager",
    actionType: "UPDATE",
    targetType: "ENTITY",
    targetId: "-",
    targetName: "-",
    messageEn: "",
    messageTr: "",
    ...entry,
  };
  activityLogs = [log, ...activityLogs];
  return log;
};

const getActorDisplayName = (actorRole) => {
  if (actorRole === "SUPER_ADMIN") return "Super Admin";
  if (actorRole === "UNIVERSITY_MANAGER") return "University Manager";
  if (actorRole === "ADVISOR") return "Advisor";
  if (actorRole === "CLUB_ADMIN") return "Club Admin";
  return "System User";
};

const roleToSystem = (role) => {
  if (!role) return "ROLE_USER";
  return role.startsWith("ROLE_") ? role : `ROLE_${role}`;
};

const buildClubStats = (club) => {
  const clubMembers = clubMemberships.filter((item) => item.clubId === club.id);
  const clubEvents = events.filter((item) => item.clubId === club.id);
  const now = new Date();
  return {
    ...club,
    totalMembers: clubMembers.length,
    totalAdmins: clubMembers.filter((item) => item.role === "ADMIN").length,
    totalModerators: clubMembers.filter((item) => item.role === "MODERATOR").length,
    totalEvents: clubEvents.length,
    upcomingEventsNumber: clubEvents.filter((item) => new Date(item.startDate) >= now).length,
    pastEventsNumber: clubEvents.filter((item) => new Date(item.startDate) < now).length,
  };
};

const getDefaultClubDetails = (campusId, universityId) => {
  const campus = getCampusById(campusId);
  const university = universities.find((item) => item.id === Number(universityId || campus?.universityId));
  return {
    campusNameLocal: campus?.nameLocal || "",
    universityNameLocal: university?.nameLocal || "",
    contactEmail: "",
    imageUrl: null,
    communication: {
      email: "",
      phone_number: "",
      instagram: "",
      twitter: "",
      whatsapp_phone_number: "",
    },
  };
};

const buildUserRecord = (raw) => {
  const campus = getCampusById(raw.campusId) || campuses.find((item) => item.universityId === raw.universityId);
  const faculty = getFacultyById(raw.facultyId) || faculties.find((item) => item.campusId === campus?.id);
  const department = getDepartmentById(raw.departmentId) || departments.find((item) => item.facultyId === faculty?.id);
  const university = universities.find((item) => item.id === (raw.universityId || campus?.universityId));
  const memberships = clubMemberships.filter((item) => item.userId === raw.id);

  return {
    id: raw.id,
    firstName: raw.firstName || (raw.fullName || "").split(" ").slice(0, -1).join(" "),
    lastName: raw.lastName || (raw.fullName || "").split(" ").slice(-1).join(" "),
    fullName:
      raw.fullName || `${raw.firstName || ""} ${raw.lastName || ""}`.trim() || raw.email || "User",
    email: raw.email,
    studentNumber: raw.studentNumber || "",
    phoneNumber: raw.phoneNumber || "",
    gender: raw.gender || "MALE",
    universityId: university?.id || raw.universityId || null,
    universityName: university?.nameLocal || university?.nameEn || "",
    universityNameEn: university?.nameEn || "",
    campusId: campus?.id || null,
    campusName: campus?.nameLocal || campus?.nameEn || "",
    campusNameEn: campus?.nameEn || "",
    facultyId: faculty?.id || null,
    facultyName: faculty?.nameLocal || faculty?.nameEn || "",
    facultyNameEn: faculty?.nameEn || "",
    departmentId: department?.id || null,
    departmentName: department?.nameLocal || department?.nameEn || "",
    departmentNameEn: department?.nameEn || "",
    educationLevel: Number(raw.educationLevel || 1),
    role: roleToSystem(raw.role || "USER"),
    emailVerified: Boolean(raw.emailVerified),
    active: raw.active !== false,
    banned: Boolean(raw.banned),
    totalEventsRegistered: events.filter((item) => item.universityId === (university?.id || raw.universityId)).length,
    totalEventsAttended: Math.floor(events.filter((item) => item.universityId === (university?.id || raw.universityId)).length / 2),
    totalTicketsConfirmed: memberships.length * 2,
    totalClubsJoined: memberships.length,
    clubRoles: memberships.map((membership) => {
      const club = clubs.find((c) => c.id === membership.clubId);
      return { clubName: club?.nameLocal || club?.nameEn || "-", clubNameEn: club?.nameEn || "-", role: membership.role };
    }),
  };
};

const mapMembership = (item) => {
  const user = users.find((candidate) => candidate.id === item.userId);
  return {
    userId: item.userId,
    role: item.role,
    joinedAt: item.joinedAt,
    fullName: user?.fullName || "-",
    email: user?.email || "-",
    studentNumber: user?.studentNumber || "-",
    universityId: user?.universityId,
  };
};

const mockService = {
  getDashboardStats: async ({ userRole, currentTenantId }) => {
    await delay();
    const scopedCampuses = filterByTenant(campuses, { userRole, currentTenantId });
    const scopedFaculties = filterByTenant(faculties, { userRole, currentTenantId });
    const scopedDepartments = filterByTenant(departments, { userRole, currentTenantId });
    const scopedClubs = filterByTenant(clubs, { userRole, currentTenantId });
    const scopedEvents = filterByTenant(events, { userRole, currentTenantId });
    const scopedTransfers = filterByTenant(transfers, { userRole, currentTenantId });
    const scopedUsers = filterByTenant(users, { userRole, currentTenantId });

    return {
      success: true,
      data: {
        totalCampuses: scopedCampuses.length,
        totalFaculties: scopedFaculties.length,
        totalDepartments: scopedDepartments.length,
        totalClubs: scopedClubs.length,
        totalEvents: scopedEvents.length,
        pendingApprovals: scopedEvents.filter((item) => item.approvalStatus === "PENDING").length,
        totalUsers: scopedUsers.length,
        totalRevenue: scopedTransfers
          .filter((item) => item.transferType === "PAYMENT" && item.status === "COMPLETED")
          .reduce((sum, item) => sum + item.amount, 0),
      },
    };
  },

  getUniversity: async (universityId) => {
    await delay();
    const result = universities.find((item) => item.id === Number(universityId));
    return result ? { success: true, data: result } : { success: false, data: null };
  },

  updateUniversity: async (universityId, data) => {
    await delay();
    const idx = universities.findIndex((item) => item.id === Number(universityId));
    if (idx === -1) return { success: false };
    universities[idx] = { ...universities[idx], ...data };
    appendActivityLog({
      universityId: Number(universityId),
      actionType: "UPDATE_UNIVERSITY",
      targetType: "UNIVERSITY",
      targetId: universities[idx].id,
      targetName: universities[idx].nameEn,
      messageEn: `University Manager updated university information for ${universities[idx].nameEn}.`,
      messageTr: `Üniversite Yöneticisi ${universities[idx].nameLocal || universities[idx].nameEn} bilgilerini güncelledi.`,
      metadata: { fields: Object.keys(data || {}) },
    });
    return { success: true, data: universities[idx] };
  },

  getCampuses: async ({ userRole, currentTenantId, page = 0, size = 10, search = "" }) => {
    await delay();
    let filtered = filterByTenant(campuses, { userRole, currentTenantId });
    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          (item.nameEn || "").toLowerCase().includes(q) ||
          (item.nameLocal || "").toLowerCase().includes(q) ||
          (item.cityName || "").toLowerCase().includes(q)
      );
    }
    return paginate(filtered, page, size);
  },

  getAllCampuses: async ({ userRole, currentTenantId }) => {
    await delay();
    return { success: true, data: filterByTenant(campuses, { userRole, currentTenantId }) };
  },

  createCampus: async ({ userRole, currentTenantId, data }) => {
    await delay();
    const universityId = Number(data.universityId);
    if (userRole === "UNIVERSITY_MANAGER" && currentTenantId != null && universityId !== Number(currentTenantId)) {
      return { success: false, message: "TENANT_SCOPE_VIOLATION" };
    }

    const maxId = campuses.reduce((max, item) => Math.max(max, item.id), 0);
    const university = universities.find((item) => item.id === universityId);
    const now = new Date().toISOString();
    const created = {
      id: maxId + 1,
      universityId,
      universityNameEn: university?.nameEn || "",
      universityNameLocal: university?.nameLocal || "",
      nameEn: data.nameEn,
      nameLocal: data.nameLocal,
      cityName: data.cityName,
      countryName: data.countryName,
      active: data.active ?? true,
      studentCount: 0,
      clubCount: 0,
      eventCount: 0,
      createdAt: now,
      updatedAt: now,
    };
    campuses.push(created);
    appendActivityLog({
      universityId: created.universityId,
      actorRole: userRole === "SUPER_ADMIN" ? "SUPER_ADMIN" : "UNIVERSITY_MANAGER",
      actorName: getActorDisplayName(userRole),
      actionType: "CREATE_CAMPUS",
      targetType: "CAMPUS",
      targetId: created.id,
      targetName: created.nameEn,
      messageEn: `${getActorDisplayName(userRole)} created campus ${created.nameEn}.`,
      messageTr: `${getActorDisplayName(userRole)} ${created.nameLocal || created.nameEn} kampüsünü oluşturdu.`,
    });
    return { success: true, data: created };
  },

  updateCampus: async ({ id, userRole, currentTenantId, data }) => {
    await delay();
    const idx = campuses.findIndex((item) => item.id === Number(id));
    if (idx === -1) return { success: false, message: "NOT_FOUND" };
    if (userRole === "UNIVERSITY_MANAGER" && currentTenantId != null && Number(campuses[idx].universityId) !== Number(currentTenantId)) {
      return { success: false, message: "TENANT_SCOPE_VIOLATION" };
    }

    const nextUniversityId = Number(data.universityId ?? campuses[idx].universityId);
    const university = universities.find((item) => item.id === nextUniversityId);
    campuses[idx] = {
      ...campuses[idx],
      ...data,
      universityId: nextUniversityId,
      universityNameEn: university?.nameEn || campuses[idx].universityNameEn,
      universityNameLocal: university?.nameLocal || campuses[idx].universityNameLocal,
      updatedAt: new Date().toISOString(),
    };
    appendActivityLog({
      universityId: campuses[idx].universityId,
      actorRole: userRole === "SUPER_ADMIN" ? "SUPER_ADMIN" : "UNIVERSITY_MANAGER",
      actorName: getActorDisplayName(userRole),
      actionType: "UPDATE_CAMPUS",
      targetType: "CAMPUS",
      targetId: campuses[idx].id,
      targetName: campuses[idx].nameEn,
      messageEn: `${getActorDisplayName(userRole)} updated campus ${campuses[idx].nameEn}.`,
      messageTr: `${getActorDisplayName(userRole)} ${campuses[idx].nameLocal || campuses[idx].nameEn} kampüsünü güncelledi.`,
      metadata: { fields: Object.keys(data || {}) },
    });
    return { success: true, data: campuses[idx] };
  },

  deleteCampus: async ({ id, userRole, currentTenantId }) => {
    await delay();
    const campus = getCampusById(id);
    if (!campus) return { success: false, message: "NOT_FOUND" };
    if (userRole === "UNIVERSITY_MANAGER" && currentTenantId != null && Number(campus.universityId) !== Number(currentTenantId)) {
      return { success: false, message: "TENANT_SCOPE_VIOLATION" };
    }

    const hasDependencies =
      faculties.some((item) => item.campusId === campus.id) ||
      departments.some((item) => item.campusId === campus.id) ||
      clubs.some((item) => item.campusId === campus.id) ||
      events.some((item) => item.campusId === campus.id);

    if (hasDependencies) return { success: false, message: "CAMPUS_HAS_DEPENDENCIES" };

    campuses = campuses.filter((item) => item.id !== campus.id);
    appendActivityLog({
      universityId: campus.universityId,
      actorRole: userRole === "SUPER_ADMIN" ? "SUPER_ADMIN" : "UNIVERSITY_MANAGER",
      actorName: getActorDisplayName(userRole),
      actionType: "DELETE_CAMPUS",
      targetType: "CAMPUS",
      targetId: campus.id,
      targetName: campus.nameEn,
      messageEn: `${getActorDisplayName(userRole)} deleted campus ${campus.nameEn}.`,
      messageTr: `${getActorDisplayName(userRole)} ${campus.nameLocal || campus.nameEn} kampüsünü sildi.`,
    });
    return { success: true };
  },

  getFaculties: async ({ userRole, currentTenantId, campusId, page = 0, size = 10, search = "" }) => {
    await delay();
    let filtered = filterByTenant(faculties, { userRole, currentTenantId });
    if (campusId) filtered = filtered.filter((item) => item.campusId === Number(campusId));
    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          (item.nameEn || "").toLowerCase().includes(q) ||
          (item.nameLocal || "").toLowerCase().includes(q)
      );
    }
    return paginate(filtered, page, size);
  },

  getAllFaculties: async ({ userRole, currentTenantId }) => {
    await delay();
    return { success: true, data: filterByTenant(faculties, { userRole, currentTenantId }) };
  },

  createFaculty: async ({ userRole, currentTenantId, data }) => {
    await delay();
    const campus = getCampusById(data.campusId);
    if (!campus) return { success: false, message: "CAMPUS_NOT_FOUND" };
    if (userRole === "UNIVERSITY_MANAGER" && currentTenantId != null && Number(campus.universityId) !== Number(currentTenantId)) {
      return { success: false, message: "TENANT_SCOPE_VIOLATION" };
    }

    const maxId = faculties.reduce((max, item) => Math.max(max, item.id), 0);
    const created = {
      id: maxId + 1,
      universityId: campus.universityId,
      universityNameEn: campus.universityNameEn,
      universityNameLocal: campus.universityNameLocal,
      campusId: campus.id,
      campusNameEn: campus.nameEn,
      campusNameLocal: campus.nameLocal,
      nameEn: data.nameEn,
      nameLocal: data.nameLocal,
      active: data.active ?? true,
      createdAt: new Date().toISOString(),
    };
    faculties.push(created);
    appendActivityLog({
      universityId: created.universityId,
      actorRole: userRole === "SUPER_ADMIN" ? "SUPER_ADMIN" : "UNIVERSITY_MANAGER",
      actorName: getActorDisplayName(userRole),
      actionType: "CREATE_FACULTY",
      targetType: "FACULTY",
      targetId: created.id,
      targetName: created.nameEn,
      messageEn: `${getActorDisplayName(userRole)} created faculty ${created.nameEn}.`,
      messageTr: `${getActorDisplayName(userRole)} ${created.nameLocal || created.nameEn} fakültesini oluşturdu.`,
    });
    return { success: true, data: created };
  },

  updateFaculty: async ({ id, userRole, currentTenantId, data }) => {
    await delay();
    const idx = faculties.findIndex((item) => item.id === Number(id));
    if (idx === -1) return { success: false, message: "NOT_FOUND" };
    const current = faculties[idx];

    if (userRole === "UNIVERSITY_MANAGER" && currentTenantId != null && Number(current.universityId) !== Number(currentTenantId)) {
      return { success: false, message: "TENANT_SCOPE_VIOLATION" };
    }

    const campus = getCampusById(data.campusId ?? current.campusId);
    if (!campus) return { success: false, message: "CAMPUS_NOT_FOUND" };

    faculties[idx] = {
      ...current,
      ...data,
      campusId: campus.id,
      campusNameEn: campus.nameEn,
      campusNameLocal: campus.nameLocal,
      universityId: campus.universityId,
      universityNameEn: campus.universityNameEn,
      universityNameLocal: campus.universityNameLocal,
    };

    departments = departments.map((item) =>
      item.facultyId === current.id
        ? {
            ...item,
            campusId: campus.id,
            campusNameEn: campus.nameEn,
            campusNameLocal: campus.nameLocal,
            universityId: campus.universityId,
            universityNameEn: campus.universityNameEn,
            universityNameLocal: campus.universityNameLocal,
            facultyNameEn: faculties[idx].nameEn,
            facultyNameLocal: faculties[idx].nameLocal,
          }
        : item
    );

    appendActivityLog({
      universityId: faculties[idx].universityId,
      actorRole: userRole === "SUPER_ADMIN" ? "SUPER_ADMIN" : "UNIVERSITY_MANAGER",
      actorName: getActorDisplayName(userRole),
      actionType: "UPDATE_FACULTY",
      targetType: "FACULTY",
      targetId: faculties[idx].id,
      targetName: faculties[idx].nameEn,
      messageEn: `${getActorDisplayName(userRole)} updated faculty ${faculties[idx].nameEn}.`,
      messageTr: `${getActorDisplayName(userRole)} ${faculties[idx].nameLocal || faculties[idx].nameEn} fakültesini güncelledi.`,
      metadata: { fields: Object.keys(data || {}) },
    });
    return { success: true, data: faculties[idx] };
  },

  deleteFaculty: async ({ id, userRole, currentTenantId }) => {
    await delay();
    const faculty = getFacultyById(id);
    if (!faculty) return { success: false, message: "NOT_FOUND" };
    if (userRole === "UNIVERSITY_MANAGER" && currentTenantId != null && Number(faculty.universityId) !== Number(currentTenantId)) {
      return { success: false, message: "TENANT_SCOPE_VIOLATION" };
    }
    if (departments.some((item) => item.facultyId === faculty.id)) {
      return { success: false, message: "FACULTY_HAS_DEPENDENCIES" };
    }
    faculties = faculties.filter((item) => item.id !== faculty.id);
    appendActivityLog({
      universityId: faculty.universityId,
      actorRole: userRole === "SUPER_ADMIN" ? "SUPER_ADMIN" : "UNIVERSITY_MANAGER",
      actorName: getActorDisplayName(userRole),
      actionType: "DELETE_FACULTY",
      targetType: "FACULTY",
      targetId: faculty.id,
      targetName: faculty.nameEn,
      messageEn: `${getActorDisplayName(userRole)} deleted faculty ${faculty.nameEn}.`,
      messageTr: `${getActorDisplayName(userRole)} ${faculty.nameLocal || faculty.nameEn} fakültesini sildi.`,
    });
    return { success: true };
  },

  getDepartments: async ({ userRole, currentTenantId, campusId, facultyId, page = 0, size = 10, search = "" }) => {
    await delay();
    let filtered = filterByTenant(departments, { userRole, currentTenantId });
    if (campusId) filtered = filtered.filter((item) => item.campusId === Number(campusId));
    if (facultyId) filtered = filtered.filter((item) => item.facultyId === Number(facultyId));
    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          (item.nameEn || "").toLowerCase().includes(q) ||
          (item.nameLocal || "").toLowerCase().includes(q)
      );
    }
    return paginate(filtered, page, size);
  },

  createDepartment: async ({ userRole, currentTenantId, data }) => {
    await delay();
    const campus = getCampusById(data.campusId);
    const faculty = getFacultyById(data.facultyId);
    if (!campus || !faculty) return { success: false, message: "DEPENDENCY_NOT_FOUND" };

    if (userRole === "UNIVERSITY_MANAGER" && currentTenantId != null && Number(campus.universityId) !== Number(currentTenantId)) {
      return { success: false, message: "TENANT_SCOPE_VIOLATION" };
    }

    const maxId = departments.reduce((max, item) => Math.max(max, item.id), 0);
    const created = {
      id: maxId + 1,
      universityId: campus.universityId,
      universityNameEn: campus.universityNameEn,
      universityNameLocal: campus.universityNameLocal,
      campusId: campus.id,
      campusNameEn: campus.nameEn,
      campusNameLocal: campus.nameLocal,
      facultyId: faculty.id,
      facultyNameEn: faculty.nameEn,
      facultyNameLocal: faculty.nameLocal,
      nameEn: data.nameEn,
      nameLocal: data.nameLocal,
      studentCount: Number(data.studentCount || 0),
      active: data.active ?? true,
    };
    departments.push(created);
    appendActivityLog({
      universityId: created.universityId,
      actorRole: userRole === "SUPER_ADMIN" ? "SUPER_ADMIN" : "UNIVERSITY_MANAGER",
      actorName: getActorDisplayName(userRole),
      actionType: "CREATE_DEPARTMENT",
      targetType: "DEPARTMENT",
      targetId: created.id,
      targetName: created.nameEn,
      messageEn: `${getActorDisplayName(userRole)} created department ${created.nameEn}.`,
      messageTr: `${getActorDisplayName(userRole)} ${created.nameLocal || created.nameEn} bölümünü oluşturdu.`,
    });
    return { success: true, data: created };
  },

  updateDepartment: async ({ id, userRole, currentTenantId, data }) => {
    await delay();
    const idx = departments.findIndex((item) => item.id === Number(id));
    if (idx === -1) return { success: false, message: "NOT_FOUND" };
    const current = departments[idx];

    if (userRole === "UNIVERSITY_MANAGER" && currentTenantId != null && Number(current.universityId) !== Number(currentTenantId)) {
      return { success: false, message: "TENANT_SCOPE_VIOLATION" };
    }

    const campus = getCampusById(data.campusId ?? current.campusId);
    const faculty = getFacultyById(data.facultyId ?? current.facultyId);
    if (!campus || !faculty) return { success: false, message: "DEPENDENCY_NOT_FOUND" };

    departments[idx] = {
      ...current,
      ...data,
      campusId: campus.id,
      campusNameEn: campus.nameEn,
      campusNameLocal: campus.nameLocal,
      facultyId: faculty.id,
      facultyNameEn: faculty.nameEn,
      facultyNameLocal: faculty.nameLocal,
      universityId: campus.universityId,
      universityNameEn: campus.universityNameEn,
      universityNameLocal: campus.universityNameLocal,
      studentCount: Number(data.studentCount ?? current.studentCount ?? 0),
    };
    appendActivityLog({
      universityId: departments[idx].universityId,
      actorRole: userRole === "SUPER_ADMIN" ? "SUPER_ADMIN" : "UNIVERSITY_MANAGER",
      actorName: getActorDisplayName(userRole),
      actionType: "UPDATE_DEPARTMENT",
      targetType: "DEPARTMENT",
      targetId: departments[idx].id,
      targetName: departments[idx].nameEn,
      messageEn: `${getActorDisplayName(userRole)} updated department ${departments[idx].nameEn}.`,
      messageTr: `${getActorDisplayName(userRole)} ${departments[idx].nameLocal || departments[idx].nameEn} bölümünü güncelledi.`,
      metadata: { fields: Object.keys(data || {}) },
    });
    return { success: true, data: departments[idx] };
  },

  deleteDepartment: async ({ id, userRole, currentTenantId }) => {
    await delay();
    const department = getDepartmentById(id);
    if (!department) return { success: false, message: "NOT_FOUND" };
    if (userRole === "UNIVERSITY_MANAGER" && currentTenantId != null && Number(department.universityId) !== Number(currentTenantId)) {
      return { success: false, message: "TENANT_SCOPE_VIOLATION" };
    }
    departments = departments.filter((item) => item.id !== department.id);
    appendActivityLog({
      universityId: department.universityId,
      actorRole: userRole === "SUPER_ADMIN" ? "SUPER_ADMIN" : "UNIVERSITY_MANAGER",
      actorName: getActorDisplayName(userRole),
      actionType: "DELETE_DEPARTMENT",
      targetType: "DEPARTMENT",
      targetId: department.id,
      targetName: department.nameEn,
      messageEn: `${getActorDisplayName(userRole)} deleted department ${department.nameEn}.`,
      messageTr: `${getActorDisplayName(userRole)} ${department.nameLocal || department.nameEn} bölümünü sildi.`,
    });
    return { success: true };
  },

  getClubs: async ({ userRole, currentTenantId, campusId, page = 0, size = 10, search = "" }) => {
    await delay();
    let filtered = filterByTenant(clubs, { userRole, currentTenantId });
    if (campusId) filtered = filtered.filter((item) => item.campusId === Number(campusId));
    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          (item.nameEn || "").toLowerCase().includes(q) ||
          (item.nameLocal || "").toLowerCase().includes(q) ||
          (item.descriptionEn || "").toLowerCase().includes(q)
      );
    }
    return paginate(filtered.map(buildClubStats), page, size);
  },

  getAllClubs: async ({ userRole, currentTenantId }) => {
    await delay();
    return { success: true, data: filterByTenant(clubs, { userRole, currentTenantId }).map(buildClubStats) };
  },

  getClubById: async (id, tenantCtx) => {
    await delay();
    const club = getClubById(id);
    if (!club) return { success: false, message: "NOT_FOUND" };
    if (filterByTenant([club], tenantCtx || {}).length === 0) return { success: false, message: "TENANT_SCOPE_VIOLATION" };
    return { success: true, data: buildClubStats(club) };
  },

  createClub: async ({ userRole, currentTenantId, data }) => {
    await delay();
    const campus = getCampusById(data.campusId);
    if (!campus) return { success: false, message: "CAMPUS_NOT_FOUND" };
    if (userRole === "UNIVERSITY_MANAGER" && currentTenantId != null && Number(campus.universityId) !== Number(currentTenantId)) {
      return { success: false, message: "TENANT_SCOPE_VIOLATION" };
    }

    const maxId = clubs.reduce((max, item) => Math.max(max, item.id), 0);
    const university = universities.find((item) => item.id === campus.universityId);
    const defaults = getDefaultClubDetails(campus.id, campus.universityId);
    const created = {
      id: maxId + 1,
      universityId: campus.universityId,
      campusId: campus.id,
      nameEn: data.nameEn,
      nameLocal: data.nameLocal,
      descriptionEn: data.descriptionEn || "",
      descriptionLocal: data.descriptionLocal || "",
      campusNameEn: campus.nameEn,
      campusNameLocal: campus.nameLocal || defaults.campusNameLocal,
      universityNameEn: university?.nameEn || "",
      universityNameLocal: university?.nameLocal || defaults.universityNameLocal,
      categories: Array.isArray(data.categories) ? data.categories : [],
      contactEmail: data.contactEmail || defaults.contactEmail,
      communication: data.communication || defaults.communication,
      imageUrl: data.imageUrl || null,
      active: data.active !== false,
    };
    clubs.push(created);
    appendActivityLog({
      universityId: created.universityId,
      actorRole: userRole === "SUPER_ADMIN" ? "SUPER_ADMIN" : "UNIVERSITY_MANAGER",
      actorName: getActorDisplayName(userRole),
      actionType: "CREATE_CLUB",
      targetType: "CLUB",
      targetId: created.id,
      targetName: created.nameEn,
      messageEn: `${getActorDisplayName(userRole)} created club ${created.nameEn}.`,
      messageTr: `${getActorDisplayName(userRole)} ${created.nameLocal || created.nameEn} kulübünü oluşturdu.`,
    });
    return { success: true, data: buildClubStats(created) };
  },

  updateClub: async ({ id, userRole, currentTenantId, data }) => {
    await delay();
    const idx = clubs.findIndex((item) => item.id === Number(id));
    if (idx === -1) return { success: false, message: "NOT_FOUND" };
    const current = clubs[idx];
    if (userRole === "UNIVERSITY_MANAGER" && currentTenantId != null && Number(current.universityId) !== Number(currentTenantId)) {
      return { success: false, message: "TENANT_SCOPE_VIOLATION" };
    }

    const campus = data.campusId ? getCampusById(data.campusId) : getCampusById(current.campusId);
    if (!campus) return { success: false, message: "CAMPUS_NOT_FOUND" };
    const university = universities.find((item) => item.id === campus.universityId);
    const defaults = getDefaultClubDetails(campus.id, campus.universityId);

    clubs[idx] = {
      ...current,
      ...data,
      campusId: campus.id,
      campusNameEn: campus.nameEn,
      campusNameLocal: campus.nameLocal || defaults.campusNameLocal,
      universityId: campus.universityId,
      universityNameEn: university?.nameEn || current.universityNameEn,
      universityNameLocal: university?.nameLocal || current.universityNameLocal || defaults.universityNameLocal,
      categories: Array.isArray(data.categories) ? data.categories : current.categories,
      contactEmail: data.contactEmail ?? current.contactEmail ?? defaults.contactEmail,
      communication: data.communication || current.communication || defaults.communication,
    };
    appendActivityLog({
      universityId: clubs[idx].universityId,
      actorRole: userRole === "SUPER_ADMIN" ? "SUPER_ADMIN" : "UNIVERSITY_MANAGER",
      actorName: getActorDisplayName(userRole),
      actionType: "UPDATE_CLUB",
      targetType: "CLUB",
      targetId: clubs[idx].id,
      targetName: clubs[idx].nameEn,
      messageEn: `${getActorDisplayName(userRole)} updated club ${clubs[idx].nameEn}.`,
      messageTr: `${getActorDisplayName(userRole)} ${clubs[idx].nameLocal || clubs[idx].nameEn} kulübünü güncelledi.`,
      metadata: { fields: Object.keys(data || {}) },
    });
    return { success: true, data: buildClubStats(clubs[idx]) };
  },

  deleteClub: async ({ id, userRole, currentTenantId }) => {
    await delay();
    const club = getClubById(id);
    if (!club) return { success: false, message: "NOT_FOUND" };
    if (userRole === "UNIVERSITY_MANAGER" && currentTenantId != null && Number(club.universityId) !== Number(currentTenantId)) {
      return { success: false, message: "TENANT_SCOPE_VIOLATION" };
    }

    clubs = clubs.filter((item) => item.id !== Number(id));
    events = events.filter((item) => item.clubId !== Number(id));
    clubMemberships = clubMemberships.filter((item) => item.clubId !== Number(id));
    appendActivityLog({
      universityId: club.universityId,
      actorRole: userRole === "SUPER_ADMIN" ? "SUPER_ADMIN" : "UNIVERSITY_MANAGER",
      actorName: getActorDisplayName(userRole),
      actionType: "DELETE_CLUB",
      targetType: "CLUB",
      targetId: club.id,
      targetName: club.nameEn,
      messageEn: `${getActorDisplayName(userRole)} deleted club ${club.nameEn}.`,
      messageTr: `${getActorDisplayName(userRole)} ${club.nameLocal || club.nameEn} kulübünü sildi.`,
    });
    return { success: true };
  },

  toggleClubStatus: async ({ id, userRole, currentTenantId, active }) => {
    await delay();
    const idx = clubs.findIndex((item) => item.id === Number(id));
    if (idx === -1) return { success: false, message: "NOT_FOUND" };
    if (userRole === "UNIVERSITY_MANAGER" && currentTenantId != null && Number(clubs[idx].universityId) !== Number(currentTenantId)) {
      return { success: false, message: "TENANT_SCOPE_VIOLATION" };
    }
    clubs[idx] = { ...clubs[idx], active: Boolean(active) };
    appendActivityLog({
      universityId: clubs[idx].universityId,
      actorRole: userRole === "SUPER_ADMIN" ? "SUPER_ADMIN" : "UNIVERSITY_MANAGER",
      actorName: getActorDisplayName(userRole),
      actionType: "TOGGLE_CLUB_STATUS",
      targetType: "CLUB",
      targetId: clubs[idx].id,
      targetName: clubs[idx].nameEn,
      messageEn: `${getActorDisplayName(userRole)} ${clubs[idx].active ? "activated" : "deactivated"} club ${clubs[idx].nameEn}.`,
      messageTr: `${getActorDisplayName(userRole)} ${clubs[idx].nameLocal || clubs[idx].nameEn} kulübünü ${clubs[idx].active ? "aktif" : "pasif"} yaptı.`,
      metadata: { active: clubs[idx].active },
    });
    return { success: true, data: buildClubStats(clubs[idx]) };
  },

  getClubMembers: async ({ clubId, userRole, currentTenantId, page = 0, size = 10, search = "" }) => {
    await delay();
    const club = getClubById(clubId);
    if (!club) return { success: false, message: "NOT_FOUND" };
    if (filterByTenant([club], { userRole, currentTenantId }).length === 0) return { success: false, message: "TENANT_SCOPE_VIOLATION" };

    let records = clubMemberships.filter((item) => item.clubId === Number(clubId)).map(mapMembership);
    if (search) {
      const q = search.toLowerCase();
      records = records.filter(
        (item) =>
          (item.fullName || "").toLowerCase().includes(q) ||
          (item.email || "").toLowerCase().includes(q) ||
          (item.studentNumber || "").toLowerCase().includes(q)
      );
    }
    return paginate(records, page, size);
  },

  addClubMember: async ({ clubId, userId, role }, tenantCtx) => {
    await delay();
    const club = getClubById(clubId);
    const user = users.find((item) => item.id === Number(userId));
    if (!club || !user) return { success: false, message: "NOT_FOUND" };
    if (filterByTenant([club], tenantCtx || {}).length === 0) return { success: false, message: "TENANT_SCOPE_VIOLATION" };
    if (Number(user.universityId) !== Number(club.universityId)) return { success: false, message: "CROSS_TENANT_USER" };
    if (clubMemberships.some((item) => item.clubId === Number(clubId) && item.userId === Number(userId))) {
      return { success: false, message: "MEMBER_ALREADY_EXISTS" };
    }
    const created = { clubId: Number(clubId), userId: Number(userId), role: role || "MEMBER", joinedAt: new Date().toISOString() };
    clubMemberships.push(created);
    appendActivityLog({
      universityId: club.universityId,
      actionType: "ADD_CLUB_MEMBER",
      targetType: "CLUB",
      targetId: club.id,
      targetName: club.nameEn,
      messageEn: `University Manager added ${user.fullName || user.email} to ${club.nameEn}.`,
      messageTr: `Üniversite Yöneticisi ${user.fullName || user.email} kullanıcısını ${club.nameEn} kulübüne ekledi.`,
      metadata: { memberRole: created.role },
    });
    return { success: true, data: mapMembership(created) };
  },

  updateClubMemberRole: async ({ clubId, userId, role }, tenantCtx) => {
    await delay();
    const club = getClubById(clubId);
    if (!club) return { success: false, message: "NOT_FOUND" };
    if (filterByTenant([club], tenantCtx || {}).length === 0) return { success: false, message: "TENANT_SCOPE_VIOLATION" };
    const idx = clubMemberships.findIndex((item) => item.clubId === Number(clubId) && item.userId === Number(userId));
    if (idx === -1) return { success: false, message: "NOT_FOUND" };
    clubMemberships[idx] = { ...clubMemberships[idx], role };
    const user = getUserById(userId);
    if (club && user) {
      appendActivityLog({
        universityId: club.universityId,
        actionType: "UPDATE_CLUB_MEMBER_ROLE",
        targetType: "CLUB",
        targetId: club.id,
        targetName: club.nameEn,
        messageEn: `University Manager updated member role for ${user.fullName || user.email} in ${club.nameEn}.`,
        messageTr: `Üniversite Yöneticisi ${club.nameEn} kulübünde ${user.fullName || user.email} kullanıcısının rolünü güncelledi.`,
        metadata: { role },
      });
    }
    return { success: true, data: mapMembership(clubMemberships[idx]) };
  },

  removeClubMember: async ({ clubId, userId }, tenantCtx) => {
    await delay();
    const club = getClubById(clubId);
    if (!club) return { success: false, message: "NOT_FOUND" };
    if (filterByTenant([club], tenantCtx || {}).length === 0) return { success: false, message: "TENANT_SCOPE_VIOLATION" };
    const user = getUserById(userId);
    clubMemberships = clubMemberships.filter((item) => !(item.clubId === Number(clubId) && item.userId === Number(userId)));
    if (user) {
      appendActivityLog({
        universityId: club.universityId,
        actionType: "REMOVE_CLUB_MEMBER",
        targetType: "CLUB",
        targetId: club.id,
        targetName: club.nameEn,
        messageEn: `University Manager removed ${user.fullName || user.email} from ${club.nameEn}.`,
        messageTr: `Üniversite Yöneticisi ${user.fullName || user.email} kullanıcısını ${club.nameEn} kulübünden çıkardı.`,
      });
    }
    return { success: true };
  },

  getEvents: async ({ userRole, currentTenantId, approvalStatus, eventStatus, clubId, startDate, endDate, page = 0, size = 10 }) => {
    await delay();
    let filtered = filterByTenant(events, { userRole, currentTenantId });
    if (approvalStatus) filtered = filtered.filter((item) => item.approvalStatus === approvalStatus);
    if (eventStatus) filtered = filtered.filter((item) => item.eventStatus === eventStatus);
    if (clubId) filtered = filtered.filter((item) => item.clubId === Number(clubId));

    const start = toDateStart(startDate);
    const end = toDateEnd(endDate);
    if (start) filtered = filtered.filter((item) => new Date(item.startDate) >= start);
    if (end) filtered = filtered.filter((item) => new Date(item.startDate) <= end);

    return paginate(filtered, page, size);
  },

  approveEvent: async (eventId) => {
    await delay();
    const idx = events.findIndex((item) => item.id === Number(eventId));
    if (idx === -1) return { success: false };
    events[idx] = { ...events[idx], approvalStatus: "APPROVED" };
    appendActivityLog({
      universityId: events[idx].universityId,
      actionType: "APPROVE_EVENT",
      targetType: "EVENT",
      targetId: events[idx].id,
      targetName: events[idx].titleEn,
      messageEn: `University Manager approved event ${events[idx].titleEn}.`,
      messageTr: `Üniversite Yöneticisi ${events[idx].titleLocal || events[idx].titleEn} etkinliğini onayladı.`,
      metadata: { clubId: events[idx].clubId, clubName: events[idx].clubNameEn },
    });
    return { success: true, data: events[idx] };
  },

  rejectEvent: async (eventId, reason) => {
    await delay();
    const idx = events.findIndex((item) => item.id === Number(eventId));
    if (idx === -1) return { success: false };
    events[idx] = { ...events[idx], approvalStatus: "REJECTED", rejectionReason: reason };
    appendActivityLog({
      universityId: events[idx].universityId,
      actionType: "REJECT_EVENT",
      targetType: "EVENT",
      targetId: events[idx].id,
      targetName: events[idx].titleEn,
      messageEn: `University Manager rejected event ${events[idx].titleEn}.`,
      messageTr: `Üniversite Yöneticisi ${events[idx].titleLocal || events[idx].titleEn} etkinliğini reddetti.`,
      metadata: { reason: sanitizeText(reason) },
    });
    return { success: true, data: events[idx] };
  },

  deleteClubEvent: async ({ clubId, eventId }, tenantCtx) => {
    await delay();
    const club = getClubById(clubId);
    if (!club) return { success: false, message: "NOT_FOUND" };
    if (filterByTenant([club], tenantCtx || {}).length === 0) return { success: false, message: "TENANT_SCOPE_VIOLATION" };
    const event = events.find((item) => item.clubId === Number(clubId) && item.id === Number(eventId));
    events = events.filter((item) => !(item.clubId === Number(clubId) && item.id === Number(eventId)));
    if (event) {
      appendActivityLog({
        universityId: club.universityId,
        actionType: "DELETE_EVENT",
        targetType: "EVENT",
        targetId: event.id,
        targetName: event.titleEn,
        messageEn: `University Manager deleted event ${event.titleEn}.`,
        messageTr: `Üniversite Yöneticisi ${event.titleLocal || event.titleEn} etkinliğini sildi.`,
        metadata: { clubId: club.id, clubName: club.nameEn },
      });
    }
    return { success: true };
  },

  createEvent: async ({ userRole, currentTenantId, data } = {}) => {
    await delay();
    const club = data.clubId ? getClubById(data.clubId) : null;
    const campus = data.campusId ? getCampusById(data.campusId) : club ? getCampusById(club.campusId) : null;
    if (!club && !campus) return { success: false, message: "NOT_FOUND" };
    const university = universities.find((item) => item.id === campus?.universityId);
    const maxId = events.reduce((max, item) => Math.max(max, item.id), 0);
    const created = {
      ...data,
      id: maxId + 1,
      clubId: club ? club.id : null,
      campusId: campus ? campus.id : null,
      universityId: campus?.universityId,
      clubNameEn: club?.nameEn || null,
      clubNameLocal: club?.nameLocal || null,
      campusNameEn: campus?.nameEn || "",
      campusNameLocal: campus?.nameLocal || "",
      universityNameEn: university?.nameEn || "",
      universityNameLocal: university?.nameLocal || "",
      isUniversityEvent: !club,
      eventStatus: "UPCOMING",
      approvalStatus: "APPROVED",
      status: true,
      ticketAttendants: Array.isArray(data.ticketAttendants) ? data.ticketAttendants : [],
      createdAt: new Date().toISOString(),
    };
    events.push(created);
    appendActivityLog({
      universityId: created.universityId,
      actorRole: userRole === "SUPER_ADMIN" ? "SUPER_ADMIN" : "UNIVERSITY_MANAGER",
      actorName: getActorDisplayName(userRole),
      actionType: "CREATE_EVENT",
      targetType: "EVENT",
      targetId: created.id,
      targetName: created.titleEn,
      messageEn: `${getActorDisplayName(userRole)} created event ${created.titleEn}.`,
      messageTr: `${getActorDisplayName(userRole)} ${created.titleLocal || created.titleEn} etkinliğini oluşturdu.`,
    });
    return { success: true, data: created };
  },

  updateEvent: async ({ id, data } = {}) => {
    await delay();
    const idx = events.findIndex((item) => item.id === Number(id));
    if (idx === -1) return { success: false, message: "NOT_FOUND" };
    events[idx] = { ...events[idx], ...data };
    return { success: true, data: events[idx] };
  },

  getTicketAttendants: async (id) => {
    await delay();
    const event = events.find((item) => item.id === Number(id));
    if (!event) return { success: false, message: "NOT_FOUND" };
    const attendants = (event.ticketAttendants || []).map((email, index) => ({
      membershipId: index + 1,
      userId: index + 1,
      name: email.split("@")[0],
      email,
    }));
    return { success: true, data: attendants };
  },

  addTicketAttendants: async (id, emails) => {
    await delay();
    const idx = events.findIndex((item) => item.id === Number(id));
    if (idx === -1) return { success: false, message: "NOT_FOUND" };
    const existing = new Set(events[idx].ticketAttendants || []);
    (emails || []).forEach((email) => existing.add(email));
    events[idx] = { ...events[idx], ticketAttendants: Array.from(existing) };
    return { success: true };
  },

  removeTicketAttendant: async (id, email) => {
    await delay();
    const idx = events.findIndex((item) => item.id === Number(id));
    if (idx === -1) return { success: false, message: "NOT_FOUND" };
    events[idx] = {
      ...events[idx],
      ticketAttendants: (events[idx].ticketAttendants || []).filter((item) => item !== email),
    };
    return { success: true };
  },

  getTransfers: async ({ userRole, currentTenantId, type, status, transferType, clubId, dateFrom, dateTo, page = 0, size = 10 }) => {
    await delay();
    let filtered = filterByTenant(transfers, { userRole, currentTenantId });
    if (type) filtered = filtered.filter((item) => item.type === type);
    if (status) filtered = filtered.filter((item) => item.status === status);
    if (transferType) filtered = filtered.filter((item) => item.transferType === transferType);
    if (clubId) filtered = filtered.filter((item) => item.clubId === Number(clubId));
    if (dateFrom) filtered = filtered.filter((item) => item.date >= dateFrom);
    if (dateTo) filtered = filtered.filter((item) => item.date <= dateTo);
    return paginate(filtered, page, size);
  },

  getManagerReports: async ({
    userRole,
    currentTenantId,
    dateFrom,
    dateTo,
    campusId,
    clubId,
    eventStatus,
    approvalStatus,
    transferStatus,
    transferType,
  }) => {
    await delay();

    const scopedEvents = filterByTenant(events, { userRole, currentTenantId });
    const scopedClubs = filterByTenant(clubs, { userRole, currentTenantId });
    const scopedCampuses = filterByTenant(campuses, { userRole, currentTenantId });
    const scopedTransfers = filterByTenant(transfers, { userRole, currentTenantId });
    const scopedClubIds = new Set(scopedClubs.map((item) => item.id));

    const start = toDateStart(dateFrom);
    const end = toDateEnd(dateTo);

    let filteredEvents = scopedEvents.filter((item) => scopedClubIds.has(item.clubId));
    if (campusId) filteredEvents = filteredEvents.filter((item) => item.campusId === Number(campusId));
    if (clubId) filteredEvents = filteredEvents.filter((item) => item.clubId === Number(clubId));
    if (eventStatus) filteredEvents = filteredEvents.filter((item) => item.eventStatus === eventStatus);
    if (approvalStatus) filteredEvents = filteredEvents.filter((item) => item.approvalStatus === approvalStatus);
    if (start) filteredEvents = filteredEvents.filter((item) => new Date(item.startDate) >= start);
    if (end) filteredEvents = filteredEvents.filter((item) => new Date(item.startDate) <= end);

    let filteredTransfers = scopedTransfers.filter((item) => scopedClubIds.has(item.clubId));
    if (campusId) {
      const campusClubIds = new Set(scopedClubs.filter((item) => item.campusId === Number(campusId)).map((item) => item.id));
      filteredTransfers = filteredTransfers.filter((item) => campusClubIds.has(item.clubId));
    }
    if (clubId) filteredTransfers = filteredTransfers.filter((item) => item.clubId === Number(clubId));
    if (transferStatus) filteredTransfers = filteredTransfers.filter((item) => item.status === transferStatus);
    if (transferType) filteredTransfers = filteredTransfers.filter((item) => item.transferType === transferType);
    if (start) filteredTransfers = filteredTransfers.filter((item) => new Date(`${item.date}T00:00:00`) >= start);
    if (end) filteredTransfers = filteredTransfers.filter((item) => new Date(`${item.date}T00:00:00`) <= end);

    const clubEventCountMap = new Map();
    filteredEvents.forEach((item) => {
      const previous = clubEventCountMap.get(item.clubId) || 0;
      clubEventCountMap.set(item.clubId, previous + 1);
    });

    const topClubs = Array.from(clubEventCountMap.entries())
      .map(([clubIdValue, count]) => {
        const clubRecord = scopedClubs.find((item) => item.id === Number(clubIdValue));
        const transferTotal = filteredTransfers
          .filter((transferItem) => transferItem.clubId === Number(clubIdValue) && transferItem.status === "COMPLETED")
          .reduce((sum, transferItem) => sum + Number(transferItem.amount || 0), 0);
        const memberCount = clubMemberships.filter((membership) => membership.clubId === Number(clubIdValue)).length;

        return {
          clubId: Number(clubIdValue),
          clubName: clubRecord?.nameEn || "-",
          campusName: clubRecord?.campusNameEn || "-",
          eventCount: count,
          memberCount,
          transferTotal,
        };
      })
      .sort((a, b) => b.eventCount - a.eventCount)
      .slice(0, 10);

    const eventStatusMap = new Map();
    filteredEvents.forEach((item) => {
      const key = `${item.eventStatus}_${item.approvalStatus}`;
      const previous = eventStatusMap.get(key) || 0;
      eventStatusMap.set(key, previous + 1);
    });

    const eventStatusBreakdown = Array.from(eventStatusMap.entries()).map(([key, count]) => {
      const [statusValue, approvalValue] = key.split("_");
      return { eventStatus: statusValue, approvalStatus: approvalValue, count };
    });

    const transferStatusMap = new Map();
    filteredTransfers.forEach((item) => {
      const key = `${item.status}_${item.transferType}`;
      const previous = transferStatusMap.get(key) || 0;
      transferStatusMap.set(key, previous + 1);
    });

    const transferStatusBreakdown = Array.from(transferStatusMap.entries()).map(([key, count]) => {
      const [statusValue, typeValue] = key.split("_");
      return { status: statusValue, transferType: typeValue, count };
    });

    const approvalTrendCount = { PENDING: 0, APPROVED: 0, REJECTED: 0 };
    filteredEvents.forEach((item) => {
      approvalTrendCount[item.approvalStatus] = (approvalTrendCount[item.approvalStatus] || 0) + 1;
    });

    const transferAmount = filteredTransfers
      .filter((item) => item.transferType === "PAYMENT" && item.status === "COMPLETED")
      .reduce((sum, item) => sum + Number(item.amount || 0), 0);

    const relatedClubIds = new Set(filteredEvents.map((item) => item.clubId));
    filteredTransfers.forEach((item) => relatedClubIds.add(item.clubId));
    const totalMembers = Array.from(relatedClubIds).reduce(
      (sum, clubIdValue) => sum + clubMemberships.filter((membership) => membership.clubId === Number(clubIdValue)).length,
      0
    );

    return {
      success: true,
      data: {
        summary: {
          totalEvents: filteredEvents.length,
          pendingApprovals: filteredEvents.filter((item) => item.approvalStatus === "PENDING").length,
          completedEvents: filteredEvents.filter((item) => item.eventStatus === "COMPLETED").length,
          activeClubs: scopedClubs.filter((item) => item.active).length,
          totalMembers,
          transferCount: filteredTransfers.length,
          transferAmount,
        },
        charts: {
          eventTrend: buildMonthlyTrend(filteredEvents, "startDate"),
          approvalTrend: Object.entries(approvalTrendCount).map(([status, count]) => ({ status, count })),
          transferTrend: buildMonthlyTrend(filteredTransfers, "date", (item) => Number(item.amount || 0)),
        },
        tables: {
          topClubs,
          eventStatusBreakdown,
          transferStatusBreakdown,
        },
        filterOptions: {
          campuses: scopedCampuses.map((item) => ({ id: item.id, nameEn: item.nameEn, nameLocal: item.nameLocal })),
          clubs: scopedClubs.map((item) => ({ id: item.id, nameEn: item.nameEn, nameLocal: item.nameLocal })),
        },
      },
    };
  },

  exportReportDataset: async (payload) => {
    const report = await mockService.getManagerReports(payload);
    if (!report.success) return report;
    return {
      success: true,
      data: {
        summary: report.data.summary,
        topClubs: report.data.tables.topClubs,
        eventStatus: report.data.tables.eventStatusBreakdown,
        transferStatus: report.data.tables.transferStatusBreakdown,
      },
    };
  },

  getActivityLogs: async ({
    userRole,
    currentTenantId,
    dateFrom,
    dateTo,
    actionType,
    actorRole,
    targetType,
    search,
    page = 0,
    size = 10,
  }) => {
    await delay();
    let filtered = activityLogs.filter((item) =>
      ["UNIVERSITY_MANAGER", "SUPER_ADMIN", "ADVISOR", "CLUB_ADMIN"].includes(item.actorRole)
    );

    if (userRole === "UNIVERSITY_MANAGER" && currentTenantId != null) {
      filtered = filtered.filter((item) => Number(item.universityId) === Number(currentTenantId));
    }

    if (dateFrom) {
      const start = toDateStart(dateFrom);
      filtered = filtered.filter((item) => new Date(item.timestamp) >= start);
    }
    if (dateTo) {
      const end = toDateEnd(dateTo);
      filtered = filtered.filter((item) => new Date(item.timestamp) <= end);
    }
    if (actionType) filtered = filtered.filter((item) => item.actionType === actionType);
    if (actorRole) filtered = filtered.filter((item) => item.actorRole === actorRole);
    if (targetType) filtered = filtered.filter((item) => item.targetType === targetType);
    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter((item) =>
        [
          item.actorName,
          item.targetName,
          item.messageEn,
          item.messageTr,
          item.actionType,
          item.targetType,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(q))
      );
    }

    filtered = filtered.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    return paginate(filtered, page, size);
  },

  getMonthlyAnalytics: async ({ userRole, currentTenantId }) => {
    await delay();
    if (userRole === "SUPER_ADMIN") {
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const aggregated = months.map((month, idx) => {
        let income = 0;
        let expense = 0;
        let topClub = "";
        let topIncome = -1;
        MOCK_UNIVERSITIES.forEach((university) => {
          const row = MOCK_MONTHLY_ANALYTICS[university.id]?.[idx];
          if (!row) return;
          income += row.income;
          expense += row.expense;
          if (row.income > topIncome) {
            topIncome = row.income;
            topClub = row.topClub;
          }
        });
        return { month, income, expense, net: income - expense, topClub };
      });
      return { success: true, data: { content: aggregated, totalPages: 1, totalElements: 12 } };
    }

    const analytics = MOCK_MONTHLY_ANALYTICS[currentTenantId] || [];
    return { success: true, data: { content: analytics, totalPages: 1, totalElements: analytics.length } };
  },

  getPermissionUsers: async ({ userRole, currentTenantId, page = 0, size = 10, search = "" }) => {
    await delay();
    let filtered = filterByTenant(users, { userRole, currentTenantId });
    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          (item.fullName || "").toLowerCase().includes(q) ||
          (item.email || "").toLowerCase().includes(q) ||
          (item.studentNumber || "").toLowerCase().includes(q)
      );
    }

    return paginate(filtered, page, size);
  },

  getUsersDemo: async ({ userRole, currentTenantId, page = 0, size = 10, filters = {} }) => {
    await delay();
    let records = filterByTenant(users, { userRole, currentTenantId }).map(buildUserRecord);

    if (filters.universityId) records = records.filter((item) => String(item.universityId) === String(filters.universityId));
    if (filters.campusId) records = records.filter((item) => String(item.campusId) === String(filters.campusId));
    if (filters.emailVerified !== "") records = records.filter((item) => String(item.emailVerified) === String(filters.emailVerified));
    if (filters.banned !== "") records = records.filter((item) => String(item.banned) === String(filters.banned));
    if (filters.isActive !== "") records = records.filter((item) => String(item.active) === String(filters.isActive));

    return paginate(records, page, size);
  },

  getUserDemoDetails: async (id, tenantCtx) => {
    await delay();
    const user = users.find((item) => item.id === Number(id));
    if (!user) return { success: false, message: "NOT_FOUND" };
    if (filterByTenant([user], tenantCtx || {}).length === 0) return { success: false, message: "TENANT_SCOPE_VIOLATION" };
    return { success: true, data: buildUserRecord(user) };
  },

  createUserDemo: async ({ userRole, currentTenantId, data }) => {
    await delay();
    const universityId = Number(data.universityId);
    if (userRole === "UNIVERSITY_MANAGER" && currentTenantId != null && universityId !== Number(currentTenantId)) {
      return { success: false, message: "TENANT_SCOPE_VIOLATION" };
    }

    const maxId = users.reduce((max, item) => Math.max(max, item.id), 0);
    const created = {
      id: maxId + 1,
      ...data,
      universityId,
      campusId: Number(data.campusId),
      facultyId: data.facultyId ? Number(data.facultyId) : null,
      departmentId: data.departmentId ? Number(data.departmentId) : null,
      role: roleToSystem(data.role || "USER"),
      fullName: `${data.firstName || ""} ${data.lastName || ""}`.trim(),
      active: true,
      banned: false,
      emailVerified: false,
    };

    users.push(created);
    appendActivityLog({
      universityId: created.universityId,
      actorRole: userRole === "SUPER_ADMIN" ? "SUPER_ADMIN" : "UNIVERSITY_MANAGER",
      actorName: getActorDisplayName(userRole),
      actionType: "CREATE_USER",
      targetType: "USER",
      targetId: created.id,
      targetName: created.fullName || created.email,
      messageEn: `${getActorDisplayName(userRole)} created user ${created.fullName || created.email}.`,
      messageTr: `${getActorDisplayName(userRole)} ${created.fullName || created.email} kullanıcısını oluşturdu.`,
    });
    return { success: true, data: buildUserRecord(created) };
  },

  updateUserDemo: async ({ id, userRole, currentTenantId, data }) => {
    await delay();
    const idx = users.findIndex((item) => item.id === Number(id));
    if (idx === -1) return { success: false, message: "NOT_FOUND" };
    const current = users[idx];

    if (userRole === "UNIVERSITY_MANAGER" && currentTenantId != null && Number(current.universityId) !== Number(currentTenantId)) {
      return { success: false, message: "TENANT_SCOPE_VIOLATION" };
    }

    users[idx] = {
      ...current,
      ...data,
      universityId: data.universityId ? Number(data.universityId) : current.universityId,
      campusId: data.campusId ? Number(data.campusId) : current.campusId,
      facultyId: data.facultyId ? Number(data.facultyId) : current.facultyId,
      departmentId: data.departmentId ? Number(data.departmentId) : current.departmentId,
      fullName:
        `${data.firstName ?? current.firstName ?? ""} ${data.lastName ?? current.lastName ?? ""}`.trim() ||
        current.fullName,
    };
    appendActivityLog({
      universityId: users[idx].universityId,
      actorRole: userRole === "SUPER_ADMIN" ? "SUPER_ADMIN" : "UNIVERSITY_MANAGER",
      actorName: getActorDisplayName(userRole),
      actionType: "UPDATE_USER",
      targetType: "USER",
      targetId: users[idx].id,
      targetName: users[idx].fullName || users[idx].email,
      messageEn: `${getActorDisplayName(userRole)} updated user ${users[idx].fullName || users[idx].email}.`,
      messageTr: `${getActorDisplayName(userRole)} ${users[idx].fullName || users[idx].email} kullanıcısını güncelledi.`,
      metadata: { fields: Object.keys(data || {}) },
    });

    return { success: true, data: buildUserRecord(users[idx]) };
  },

  changeUserDemoStatusAndRole: async ({ id, userRole, currentTenantId, data }) => {
    await delay();
    const idx = users.findIndex((item) => item.id === Number(id));
    if (idx === -1) return { success: false, message: "NOT_FOUND" };

    if (userRole === "UNIVERSITY_MANAGER" && currentTenantId != null && Number(users[idx].universityId) !== Number(currentTenantId)) {
      return { success: false, message: "TENANT_SCOPE_VIOLATION" };
    }

    users[idx] = {
      ...users[idx],
      emailVerified: Boolean(data.emailVerified),
      active: Boolean(data.active),
      banned: Boolean(data.banned),
      role: roleToSystem(data.role || users[idx].role),
    };
    appendActivityLog({
      universityId: users[idx].universityId,
      actorRole: userRole === "SUPER_ADMIN" ? "SUPER_ADMIN" : "UNIVERSITY_MANAGER",
      actorName: getActorDisplayName(userRole),
      actionType: "UPDATE_USER_STATUS",
      targetType: "USER",
      targetId: users[idx].id,
      targetName: users[idx].fullName || users[idx].email,
      messageEn: `${getActorDisplayName(userRole)} changed status/role for ${users[idx].fullName || users[idx].email}.`,
      messageTr: `${getActorDisplayName(userRole)} ${users[idx].fullName || users[idx].email} kullanıcısının durum/rol bilgisini güncelledi.`,
      metadata: { emailVerified: users[idx].emailVerified, active: users[idx].active, banned: users[idx].banned, role: users[idx].role },
    });
    return { success: true, data: buildUserRecord(users[idx]) };
  },

  deleteUserDemo: async ({ id, userRole, currentTenantId }) => {
    await delay();
    const user = users.find((item) => item.id === Number(id));
    if (!user) return { success: false, message: "NOT_FOUND" };
    if (userRole === "UNIVERSITY_MANAGER" && currentTenantId != null && Number(user.universityId) !== Number(currentTenantId)) {
      return { success: false, message: "TENANT_SCOPE_VIOLATION" };
    }

    users = users.filter((item) => item.id !== Number(id));
    clubMemberships = clubMemberships.filter((item) => item.userId !== Number(id));
    appendActivityLog({
      universityId: user.universityId,
      actorRole: userRole === "SUPER_ADMIN" ? "SUPER_ADMIN" : "UNIVERSITY_MANAGER",
      actorName: getActorDisplayName(userRole),
      actionType: "DELETE_USER",
      targetType: "USER",
      targetId: user.id,
      targetName: user.fullName || user.email,
      messageEn: `${getActorDisplayName(userRole)} deleted user ${user.fullName || user.email}.`,
      messageTr: `${getActorDisplayName(userRole)} ${user.fullName || user.email} kullanıcısını sildi.`,
    });
    return { success: true };
  },

  // ── Transactions ────────────────────────────────────────────────────────────
  getTransactions: async (params = {}) => {
    await delay();
    let filtered = transactions;
    if (params.userEmail) filtered = filtered.filter(t => (t.userEmail || "").toLowerCase().includes(params.userEmail.toLowerCase()));
    if (params.startDate) filtered = filtered.filter(t => new Date(t.createdAt) >= new Date(params.startDate));
    if (params.endDate) filtered = filtered.filter(t => new Date(t.createdAt) <= new Date(params.endDate));
    if (params.status) filtered = filtered.filter(t => t.status === params.status);
    if (params.type) filtered = filtered.filter(t => t.transactionType === params.type);
    if (params.eventId) filtered = filtered.filter(t => String(t.eventId) === String(params.eventId));
    if (params.clubId) filtered = filtered.filter(t => String(t.clubId) === String(params.clubId));

    // sorting by createdAt desc
    filtered = filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return paginate(filtered, params.page || 0, params.size || 20);
  },

  getTransactionById: async (id) => {
    await delay();
    const txn = transactions.find(t => t.transactionId === id);
    if (!txn) return { success: false, error: "Transaction not found" };
    return { success: true, data: txn };
  },

};

export default mockService;
